import { useTheme } from '@mui/material/styles';
import { useCallback } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';

interface LineChartProps {
  data: { name: string; value: number; trend?: number }[];
}

export const LineChart = ({ data }: LineChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Custom tooltip component to prevent flickering
  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload || !payload.length) {
        return null;
      }

      return (
        <div
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '14px',
            lineHeight: '1.4',
            maxWidth: '200px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              marginBottom: '4px',
            }}
          >
            {label}
          </div>
          {payload.map((entry: any, index: number) => (
            <div key={index} style={{ color: entry.color, marginBottom: '2px' }}>
              {entry.name}: {entry.value}
            </div>
          ))}
        </div>
      );
    },
    [theme]
  );

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight="240px">
      <RechartsLineChart width={500} height={300} data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis dataKey="name" />
        <YAxis domain={['auto', 'dataMax']} tickCount={7} />
        <Tooltip
          content={CustomTooltip}
          allowEscapeViewBox={{ x: false, y: false }}
          animationDuration={0}
          isAnimationActive={false}
          offset={15}
          wrapperStyle={{
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="value"
          name={t('label.label.chart.data')}
          stroke={theme.palette.primary.main}
          fill={theme.palette.primary.main}
          activeDot={{ r: 8 }}
        />
        {data.some((item) => item.trend !== undefined) && (
          <Line
            type="monotone"
            dataKey="trend"
            name={t('label.label.chart.trend')}
            stroke={theme.palette.secondary.main}
            fill={theme.palette.secondary.main}
            strokeDasharray="5 5"
            dot={false}
            activeDot={{ r: 6 }}
          />
        )}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
};
